import { useEffect, useMemo, useState } from "react";
import { ChevronLeft, ChevronRight, Save, Sun, Moon, Receipt, Loader2, Plus, Trash2, UserRound } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { rs, qty, todayIso, addDays, fmtDate } from "@/lib/format";
import { PageHeader } from "@/components/Kpi";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";

const groupBy = (arr) => arr.reduce((m, x) => ((m[x.category] = m[x.category] || []).push(x), m), {});

const QtyInput = ({ value, onChange, testId }) => (
  <input type="number" min="0" step="0.5" inputMode="decimal" value={value ?? ""} placeholder="0"
    onChange={(e) => onChange(e.target.value === "" ? undefined : Number(e.target.value))}
    data-testid={testId} onFocus={(e) => e.target.select()}
    className="qty-input num w-20 h-9 rounded-md border border-slate-200 bg-amber-50/40 px-2 text-right text-sm focus:outline-none focus:ring-2 focus:ring-amber-500 focus:bg-white transition-colors" />
);

const SalesTable = ({ items, values, onChange, shift }) => (
  <div className="divide-y divide-slate-100">
    {Object.entries(groupBy(items)).map(([cat, list]) => (
      <div key={cat}>
        <div className="bg-slate-50 px-4 py-1.5 text-[11px] font-bold uppercase tracking-wider text-slate-500">{cat}</div>
        {list.map((it) => (
          <div key={it.id} className="flex items-center gap-3 px-4 py-2 hover:bg-amber-50/30 transition-colors">
            <div className="flex-1 min-w-0 text-sm text-slate-800 truncate">{it.name}</div>
            <div className="num text-xs text-slate-400 w-14 text-right">₹{it.rate}</div>
            <QtyInput value={values[it.id]} onChange={(v) => onChange(it.id, v)} testId={`qty-${shift}-${it.id}`} />
            <div className="num text-sm w-20 text-right font-medium text-slate-700">{values[it.id] ? rs(values[it.id] * it.rate) : "—"}</div>
          </div>
        ))}
      </div>
    ))}
  </div>
);

const PayerSelect = ({ value, payers, onChange, testId }) => (
  <select value={value || ""} onChange={(e) => onChange(e.target.value)} data-testid={testId}
    className={`h-9 w-32 rounded-md border border-slate-200 px-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-500 ${value ? "bg-white text-slate-800" : "bg-slate-50 text-slate-400"}`}>
    <option value="">— Paid by —</option>
    {payers.map((p) => <option key={p} value={p}>{p}</option>)}
  </select>
);

const ExpenseTable = ({ heads, items, payers, onAdd, onLine, onRemove }) => (
  <div className="divide-y divide-slate-100">
    {heads.map((h) => {
      const lines = items.filter((x) => x.head_id === h.id);
      const total = lines.reduce((a, x) => a + (Number(x.amount) || 0), 0);
      return (
        <div key={h.id} data-testid={`expense-head-${h.id}`}>
          <div className="flex items-center gap-3 px-4 py-2.5 bg-slate-50/70">
            <div className="flex-1 text-sm font-semibold text-slate-800">{h.name}</div>
            <div className="num text-sm font-bold text-slate-700 w-24 text-right">{total ? rs(total) : "—"}</div>
            <button onClick={() => onAdd(h.id)} data-testid={`add-expense-line-${h.id}`} title={`Add a ${h.name} entry`}
              className="flex h-8 w-8 items-center justify-center rounded-md bg-amber-600 text-white hover:bg-amber-700 transition-colors">
              <Plus className="h-4 w-4" />
            </button>
          </div>
          {lines.map((line, i) => (
            <div key={line.id} className="flex items-center gap-2 px-4 py-2 pl-8 hover:bg-amber-50/30 transition-colors" data-testid={`expense-line-${line.id}`}>
              <span className="w-5 text-xs text-slate-400 num">{i + 1}.</span>
              <PayerSelect value={line.payer} payers={payers} onChange={(v) => onLine(line.id, { payer: v })} testId={`expense-line-payer-${line.id}`} />
              <span className="text-xs text-slate-400">₹</span>
              <QtyInput value={line.amount} onChange={(v) => onLine(line.id, { amount: v })} testId={`expense-line-amount-${line.id}`} />
              <Input value={line.note || ""} placeholder="note (e.g. sabzi mandi)" className="h-9 flex-1 min-w-[100px] bg-white text-sm"
                onChange={(e) => onLine(line.id, { note: e.target.value })} data-testid={`expense-line-note-${line.id}`} />
              <button onClick={() => onRemove(line.id)} className="p-1.5 text-slate-300 hover:text-rose-600" data-testid={`delete-expense-line-${line.id}`}>
                <Trash2 className="h-4 w-4" />
              </button>
            </div>
          ))}
          {!lines.length && <div className="px-4 py-2 pl-8 text-xs text-slate-400">No {h.name.toLowerCase()} entry — tap + to add one.</div>}
        </div>
      );
    })}
  </div>
);

const PaidSummary = ({ entry, payers }) => {
  const tot = {};
  (entry.items || []).forEach((x) => { const p = x.payer || "Unassigned"; tot[p] = (tot[p] || 0) + (Number(x.amount) || 0); });
  (entry.staff_payments || []).forEach((r) => { const p = r.payer || "Unassigned"; tot[p] = (tot[p] || 0) + (r.amount || 0); });
  const names = [...payers, ...Object.keys(tot).filter((p) => !payers.includes(p))];
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm text-sm" data-testid="entry-paid-summary">
      <div className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-3">Who paid today</div>
      {names.map((p) => (
        <div key={p} className={`flex justify-between py-1 ${p === "Unassigned" ? "text-slate-400" : "text-slate-700"}`} data-testid={`entry-paid-${p}`}>
          <span>{p === "Unassigned" ? "Not assigned" : `${p} paid`}</span><span className="num font-medium">{rs(tot[p] || 0)}</span>
        </div>
      ))}
    </div>
  );
};

const StaffPayments = ({ date, rows, payers, onChanged }) => {
  const [staff, setStaff] = useState([]);
  const [f, setF] = useState({ staff_id: "", payer: payers[0] || "", item: "Cash", amount: "", note: "" });
  const [busy, setBusy] = useState(false);
  useEffect(() => { api.get("/staff").then((r) => setStaff(r.data.filter((x) => x.active !== false))); }, []);
  useEffect(() => { setF((x) => ({ ...x, payer: x.payer || payers[0] || "" })); }, [payers]);

  const add = async () => {
    setBusy(true);
    try {
      await api.post("/salary-entries", { ...f, date, amount: Number(f.amount) || 0 });
      setF({ ...f, amount: "", note: "" });
      toast.success("Added to staff salary & today's expenses");
      onChanged();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  const del = async (r) => {
    if (!window.confirm(`Remove ${r.staff_name} — ${rs(r.amount)}?`)) return;
    await api.delete(`/salary-entries/${r.id}`); onChanged();
  };
  const total = rows.reduce((a, r) => a + (r.amount || 0), 0);

  return (
    <div className="px-4 py-4 bg-sky-50/40 border-t border-slate-200" data-testid="staff-payment-section">
      <div className="flex items-center gap-2 mb-1">
        <UserRound className="h-4 w-4 text-sky-700" />
        <span className="text-sm font-semibold text-slate-800">Staff Payment</span>
        <span className="text-xs text-slate-500">— goes into that staff member's salary slip for this date</span>
      </div>
      <div className="flex flex-wrap items-end gap-2 my-3">
        <select value={f.staff_id} onChange={(e) => setF({ ...f, staff_id: e.target.value })} data-testid="staff-payment-staff-select"
          className="h-9 w-40 rounded-md border border-slate-200 bg-white px-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-500">
          <option value="">— Paid to —</option>
          {staff.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
        </select>
        <select value={f.payer} onChange={(e) => setF({ ...f, payer: e.target.value })} data-testid="staff-payment-payer-select"
          className="h-9 w-32 rounded-md border border-slate-200 bg-white px-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-500">
          {payers.map((p) => <option key={p} value={p}>{p} paid</option>)}
        </select>
        <Input value={f.item} onChange={(e) => setF({ ...f, item: e.target.value })} placeholder="Cash / Daru / Recharge" className="h-9 w-44 bg-white" data-testid="staff-payment-item-input" />
        <Input type="number" value={f.amount} onChange={(e) => setF({ ...f, amount: e.target.value })} placeholder="0" className="h-9 w-24 bg-white num text-right" data-testid="staff-payment-amount-input" />
        <Input value={f.note} onChange={(e) => setF({ ...f, note: e.target.value })} placeholder="note (optional)" className="h-9 flex-1 min-w-[120px] bg-white" data-testid="staff-payment-note-input" />
        <Button size="sm" className="h-9 bg-sky-700 hover:bg-sky-800" disabled={busy || !f.staff_id || !f.amount} onClick={add} data-testid="staff-payment-add-button">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}
        </Button>
      </div>
      {rows.length ? (
        <table className="w-full text-sm" data-testid="staff-payment-table">
          <tbody>
            {rows.map((r) => (
              <tr key={r.id} className="border-t border-slate-200/70" data-testid={`staff-payment-row-${r.id}`}>
                <td className="py-1.5 font-medium text-slate-800">{r.staff_name}</td>
                <td className="py-1.5 text-slate-500">{r.item}</td>
                <td className="py-1.5 text-xs text-sky-800">{r.payer ? `${r.payer} paid` : "—"}</td>
                <td className="py-1.5 text-right num font-semibold">{rs(r.amount)}</td>
                <td className="py-1.5 text-xs text-slate-400 pl-3">{r.note}</td>
                <td className="py-1.5 text-right"><button onClick={() => del(r)} className="p-1 text-slate-300 hover:text-rose-600" data-testid={`delete-staff-payment-${r.id}`}><Trash2 className="h-4 w-4" /></button></td>
              </tr>
            ))}
          </tbody>
          <tfoot><tr className="border-t border-slate-300 font-bold text-slate-800">
            <td className="py-1.5" colSpan={3}>Total staff payments</td>
            <td className="py-1.5 text-right num" data-testid="staff-payment-total">{rs(total)}</td><td colSpan={2} /></tr></tfoot>
        </table>
      ) : <p className="text-xs text-slate-400">No staff payment on this date yet.</p>}
    </div>
  );
};

const TABS = [
  { k: "day", label: "Day shift", icon: Sun, id: "shift-toggle-day" },
  { k: "night", label: "Night shift", icon: Moon, id: "shift-toggle-night" },
  { k: "expenses", label: "Expenses", icon: Receipt, id: "shift-toggle-expenses" },
];

const Totals = ({ t, ded }) => (
  <div className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm space-y-3 text-sm" data-testid="entry-totals">
    {[["Day shift sales", t.day], ["Night shift sales", t.night], ["Plates served", null, qty(t.plates)]].map(([l, v, s]) => (
      <div key={l} className="flex justify-between text-slate-600"><span>{l}</span><span className="num">{s ?? rs(v)}</span></div>
    ))}
    <div className="flex justify-between border-t pt-3 font-semibold text-slate-900"><span>Gross sales</span><span className="num" data-testid="entry-gross-total">{rs(t.gross)}</span></div>
    <div className="flex justify-between text-orange-700"><span>Less deduction {ded}%</span><span className="num">−{rs(t.gross * ded / 100)}</span></div>
    <div className="flex justify-between text-sky-800 font-medium"><span>Net sales</span><span className="num">{rs(t.gross * (1 - ded / 100))}</span></div>
    <div className="flex justify-between text-rose-700"><span>Daily expenses</span><span className="num" data-testid="entry-expense-total">−{rs(t.exp)}</span></div>
    <div className={`flex justify-between border-t pt-3 font-bold ${t.gross * (1 - ded / 100) - t.exp >= 0 ? "text-emerald-700" : "text-rose-700"}`}>
      <span>Day result (before fixed)</span><span className="num" data-testid="entry-day-result">{rs(t.gross * (1 - ded / 100) - t.exp)}</span>
    </div>
  </div>
);

export default function DailyEntry() {
  const [date, setDate] = useState(todayIso());
  const [items, setItems] = useState([]);
  const [heads, setHeads] = useState([]);
  const [ded, setDed] = useState(15);
  const [payers, setPayers] = useState([]);
  const [entry, setEntry] = useState(null);
  const [tab, setTab] = useState("day");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    Promise.all([api.get("/menu"), api.get("/expense-heads"), api.get("/settings")]).then(([m, h, s]) => {
      setItems(m.data.filter((x) => x.active !== false));
      setHeads(h.data.filter((x) => x.kind === "variable" && x.active !== false));
      setDed(s.data.deduction_pct);
      setPayers(s.data.payers || []);
    });
  }, []);
  useEffect(() => { setEntry(null); api.get(`/entries/${date}`).then((r) => setEntry(r.data)); }, [date]);
  const reload = () => api.get(`/entries/${date}`).then((r) => setEntry(r.data));

  const rate = useMemo(() => Object.fromEntries(items.map((i) => [i.id, i.rate])), [items]);
  const t = useMemo(() => {
    if (!entry) return { day: 0, night: 0, plates: 0, gross: 0, exp: 0 };
    const sum = (m, f) => Object.entries(m).reduce((a, [k, v]) => a + (v || 0) * f(k), 0);
    const day = sum(entry.day, (k) => rate[k] || 0), night = sum(entry.night, (k) => rate[k] || 0);
    const staffPaid = (entry.staff_payments || []).reduce((a, r) => a + (r.amount || 0), 0);
    const lines = (entry.items || []).reduce((a, x) => a + (Number(x.amount) || 0), 0);
    return { day, night, gross: day + night, plates: sum(entry.day, () => 1) + sum(entry.night, () => 1), exp: lines + staffPaid };
  }, [entry, rate]);

  const set = (k, id, v) => setEntry((e) => ({ ...e, [k]: { ...e[k], [id]: v } }));
  const addLine = (head_id) => setEntry((e) => ({ ...e, items: [...(e.items || []), { id: `new-${crypto.randomUUID()}`, head_id, amount: undefined, payer: payers[0] || "", note: "" }] }));
  const setLine = (id, patch) => setEntry((e) => ({ ...e, items: e.items.map((x) => (x.id === id ? { ...x, ...patch } : x)) }));
  const removeLine = (id) => setEntry((e) => ({ ...e, items: e.items.filter((x) => x.id !== id) }));
  const save = async () => {
    setSaving(true);
    try {
      const r = await api.put(`/entries/${date}`, { ...entry, items: (entry.items || []).filter((x) => Number(x.amount) > 0).map((x) => ({ ...x, id: x.id.startsWith("new-") ? "" : x.id, amount: Number(x.amount) })) });
      setEntry(r.data);
      toast.success(`Saved entry for ${fmtDate(date, { day: "numeric", month: "long" })}`);
    }
    catch (e) { toast.error(errMsg(e)); } finally { setSaving(false); }
  };

  return (
    <div>
      <PageHeader title="Daily Entry" subtitle="Plate counts per shift and money spent — saved per date.">
        <Button variant="outline" size="icon" onClick={() => setDate(addDays(date, -1))} data-testid="entry-prev-day"><ChevronLeft className="h-4 w-4" /></Button>
        <Input type="date" value={date} onChange={(e) => e.target.value && setDate(e.target.value)} className="w-44 bg-white" data-testid="date-picker-input" />
        <Button variant="outline" size="icon" onClick={() => setDate(addDays(date, 1))} data-testid="entry-next-day"><ChevronRight className="h-4 w-4" /></Button>
        <Button onClick={save} disabled={!entry || saving} className="bg-amber-600 hover:bg-amber-700" data-testid="save-daily-entry-button">
          {saving ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Save className="h-4 w-4 mr-1" />}Save entry
        </Button>
      </PageHeader>
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        <div className="lg:col-span-8 rounded-xl border border-slate-200 bg-white shadow-sm overflow-hidden">
          <div className="flex border-b border-slate-200 bg-slate-50/60">
            {TABS.map(({ k, label, icon: Icon, id }) => (
              <button key={k} onClick={() => setTab(k)} data-testid={id}
                className={`flex-1 flex items-center justify-center gap-2 py-3 text-sm font-semibold transition-colors ${tab === k ? "bg-white text-amber-800 border-b-2 border-amber-600" : "text-slate-500 hover:text-slate-800"}`}>
                <Icon className="h-4 w-4" />{label}
              </button>
            ))}
          </div>
          {!entry ? <div className="p-8 text-slate-500">Loading…</div> : tab === "expenses"
            ? <>
                <ExpenseTable heads={heads.filter((h) => h.special !== "staff")} items={entry.items || []} payers={payers}
                  onAdd={addLine} onLine={setLine} onRemove={removeLine} />
                <StaffPayments date={date} rows={entry.staff_payments || []} payers={payers} onChanged={reload} />
              </>
            : <SalesTable items={items} values={entry[tab]} shift={tab} onChange={(id, v) => set(tab, id, v)} />}
        </div>
        <div className="lg:col-span-4 space-y-4 lg:sticky lg:top-6 self-start">
          <Totals t={t} ded={ded} />
          {entry && <PaidSummary entry={entry} payers={payers} />}
          {entry && <Textarea placeholder="Notes for the day (optional)" value={entry.notes || ""} className="bg-white"
            onChange={(e) => setEntry({ ...entry, notes: e.target.value })} data-testid="entry-notes-input" />}
        </div>
      </div>
    </div>
  );
}
